import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ServerBaseUrlForStaticImages } from "constants";
import { Box, Paper, TextField } from "@mui/material"; // Import TextField for search input
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import MDButton from "components/MDButton";
import { FormControlLabel, Checkbox } from "@mui/material";
import ImageIcon from '@mui/icons-material/Image';

const columns = [
  { Header: "S.No.", accessor: "sno", sortable: true }, // Changed ID to S.No.
  { Header: "Business Name", accessor: "business_name", sortable: true },
  { Header: "Business Type", accessor: "business_type", sortable: true },
  { Header: "Mobile Number", accessor: "mobile_number", sortable: true },
  { Header: "Options", accessor: "options" },
];

function Approve() {
  const [vendorData, setVendorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null); // Not used, can be removed if not needed
  const email = localStorage.getItem("email");
  const navigate = useNavigate();
  const [selectedVendorImages, setSelectedVendorImages] = useState({}); // Initialize as empty object
  const [profileData, setProfileData] = useState({}); // Initialize as empty object
  const [vendorId, setVendorId] = useState(null); // Renamed from selectedVendorId for clarity
  const [checkedImages, setCheckedImages] = useState({});
  const [fullScreenImage, setFullScreenImage] = useState(null); // State to store the image URL for full screen view
  const [searchQuery, setSearchQuery] = useState(""); // State for global search query

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    disableBackNavigation();
    fetchVendorData();
  }, []);

  const imageKeys = ["vendor_photo", "shop_photo", "sgo_gps_photo"];

  // Dynamically generate proof document keys
  const proofKeys = selectedVendorImages.user_proof_doc
    ? selectedVendorImages.user_proof_doc.map((_, index) => `user_proof_doc-${index}`)
    : [];

  const allKeys = [...imageKeys, ...proofKeys];

  // Initialize checkedImages with allKeys
  useEffect(() => {
    setCheckedImages((prev) => {
      const updatedCheckedImages = { ...prev };
      allKeys.forEach((key) => {
        if (!(key in updatedCheckedImages)) {
          updatedCheckedImages[key] = false; // Default to false
        }
      });
      return updatedCheckedImages;
    });
  }, [selectedVendorImages, allKeys]); // Added allKeys to dependency array

  const handleCheckboxChange = (key, checked) => {
    setCheckedImages((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  // State to track if all checkboxes are checked
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    // Check if all relevant images are checked.
    // Ensure allKeys is populated before checking.
    const isAllChecked = allKeys.length > 0 && allKeys.every((key) => checkedImages[key] === true);
    setAllChecked(isAllChecked);
  }, [checkedImages, allKeys]);

  const fetchVendorData = async () => {
    try {
      const res = await api.get(`/vendor_register/fetch_pending`, {
        params: { email },
      });
      setVendorData(res.data.vendors || []); // Set vendor data from the response

      // Adjust page if current page is out of bounds after fetching new data
      if (page * rowsPerPage >= (res.data.vendors || []).length && (res.data.vendors || []).length > 0) {
        setPage(0);
      } else if ((res.data.vendors || []).length === 0) {
        setPage(0);
      }
    } catch (error) {
      console.error("Error fetching business data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch vendor data. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setFullScreenImage(imageUrl); // Set the clicked image URL
  };

  const handleViewMedia = (vendor) => {
    setVendorId(vendor.id);
    setSelectedVendorImages(vendor);
    setOpenViewModal(true);
  };

  const handleViewProfile = (vendor) => {
    setProfileData(vendor);
    setOpenProfileModal(true);
  };

  const handleApprove = async () => {
    setOpenViewModal(false);
    setCheckedImages({}); // Reset checkboxes
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to approve this vendor?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, approve!",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await api.post(
          `vendor_register/approve_vendor/${vendorId}`
        );
        if (response.data.success) {
          Swal.fire({
            title: "Vendor Approved Successfully!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchVendorData(); // Refresh the list to reflect the updated status
        } else {
          Swal.fire({
            title: "Error",
            text: response.data.message,
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Error approving vendor:", error);
        Swal.fire({
          title: "Error",
          text: "An error occurred while approving the vendor.",
          icon: "error",
        });
      }
    } else {
      Swal.fire({
        title: "Cancelled",
        text: "The vendor approval has been cancelled.",
        icon: "info",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to reject this vendor?",
      input: "text",
      inputLabel: "Reason",
      inputValidator: (value) => {
        if (!value) {
          return "You need to write something!";
        }
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Confirm!",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      try {
        const rrsult = await api.put(
          `vendor_register/reject_vendor_reason/${id}`,
          {
            reason: result.value,
          }
        );
        const response = await api.post(`/vendor_register/reject_vendor/${id}`);

        if (rrsult.data.success && response.data.success) {
          Swal.fire({
            title: "Vendor Not Approved!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchVendorData();
        } else {
          Swal.fire({
            title: "Error",
            text: response.data.message,
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Error rejecting vendor:", error);
        Swal.fire({
          title: "Error",
          text: "An error occurred while rejecting the vendor.",
          icon: "error",
        });
      }
    } else {
      Swal.fire({
        title: "Cancelled",
        text: "The vendor approval has been cancelled.",
        icon: "info",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  // Filter vendor data based on search query
  const filteredVendorData = vendorData.filter((vendor) => {
    const query = searchQuery.toLowerCase();
    return (
      vendor.business_name.toLowerCase().includes(query) ||
      vendor.business_type.toLowerCase().includes(query) ||
      vendor.mobile_number.toLowerCase().includes(query) ||
      vendor.vendor_name.toLowerCase().includes(query) || // Added owner name to search
      vendor.vendor_email.toLowerCase().includes(query) // Added email to search
    );
  });

  // Pagination handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Apply pagination to filteredVendorData
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedVendorData = filteredVendorData.slice(startIndex, endIndex);


  const data = paginatedVendorData.map((vendor, index) => ({
    sno: startIndex + index + 1, // Calculate S.No.
    id: vendor.id, // Keep original ID for actions
    business_name: vendor.business_name,
    owner_name: vendor.vendor_name,
    email: vendor.vendor_email,
    business_type: vendor.business_type,
    mobile_number: vendor.mobile_number,
    account_number: vendor.account_number,
    ifsc_code: vendor.ifsc_code,
    aadhar_number: vendor.aadhar_number,
    pan_card_number: vendor.pan_card_number,
    passbook_name: vendor.passbook_name,
    gst_number: vendor.gst,
    options: (
      <div style={{ display: "flex" }}>
        <IconButton
          color="info"
          onClick={() => handleViewProfile(vendor)}
          title="View Profile"
        >
          <VisibilityIcon />
        </IconButton>
        <IconButton
          color="success"
          onClick={() => handleViewMedia(vendor)}
          title="View Images"
        >
          <ImageIcon />
        </IconButton>
        <IconButton
          color="error"
          onClick={() => handleReject(vendor.id)}
          title="Reject"
        >
          <CancelIcon />
        </IconButton>
      </div>
    ),
  }));

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton
          onLogout={() => {
            navigate("/login");
          }}
        />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Approve Vendor
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <Box pl={3}> {/* Added Box for search input */}
                  <TextField
                    label="Search Vendors"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Box>
                <MDBox p={3}>
                  <DataTable
                    table={{ columns: columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={false} // Global search handled manually above
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredVendorData.length, // Total count of filtered vendors
                    }}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* View Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} fullWidth maxWidth="md">
        {/* Styled Header */}
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1rem",
            py: 2,
          }}
        >
          Vendor Images
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9f9f9" }}>
          <Grid container spacing={2}>
            {/* Main Keys */}
            {imageKeys.map((key) =>
              selectedVendorImages[key] ? (
                <Grid item xs={12} sm={4} key={key}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      padding: 1,
                      boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 200,
                        overflow: "hidden",
                        borderRadius: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={`${ServerBaseUrlForStaticImages}services_register/media/${selectedVendorImages[key]}`}
                        alt={key}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          cursor: "pointer",
                          transition: "transform 0.2s ease",
                        }}
                        onClick={() =>
                          handleImageClick(
                            `${ServerBaseUrlForStaticImages}services_register/media/${selectedVendorImages[key]}`
                          )
                        }
                        onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                        onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
                      />
                    </Box>

                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Checkbox
                        checked={checkedImages[key] || false}
                        onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                        sx={{
                          "& .MuiSvgIcon-root": { fontSize: 24, color: "#f44336" },
                          "&.Mui-checked .MuiSvgIcon-root": { color: "#1976d2" },
                          mb: 0.5,
                        }}
                      />
                      <MDTypography
                        variant="caption"
                        align="center"
                        fontWeight="medium"
                        color="primary"
                        sx={{
                          fontSize: key.length > 20 ? "0.72rem" : "0.8rem",
                          lineHeight: 1.3,
                          wordBreak: "break-word",
                          px: 1,
                        }}
                      >
                        {key.replace("_", " ").toUpperCase()}
                      </MDTypography>
                    </Box>
                  </Box>
                </Grid>
              ) : null
            )}

            {/* User Proof Documents */}
            {selectedVendorImages.user_proof_doc &&
              selectedVendorImages.user_proof_doc.map((image, index) => {
                const key = `user_proof_doc-${index}`;
                return (
                  <Grid item xs={12} sm={4} key={key}>
                    <Box
                      sx={{
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        padding: 1,
                        boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 200,
                          overflow: "hidden",
                          borderRadius: 2,
                          mb: 1,
                        }}
                      >
                        <img
                          src={`${ServerBaseUrlForStaticImages}services_register/media/${image}`}
                          alt={key}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onClick={() =>
                            handleImageClick(
                              `${ServerBaseUrlForStaticImages}services_register/media/${image}`
                            )
                          }
                          onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                          onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
                        />
                      </Box>

                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Checkbox
                          checked={checkedImages[key] || false}
                          onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: 24, color: "#f44336" },
                            "&.Mui-checked .MuiSvgIcon-root": { color: "#1976d2" },
                            mb: 0.5,
                          }}
                        />
                        <MDTypography
                          variant="caption"
                          align="center"
                          fontWeight="medium"
                          color="primary"
                          sx={{
                            fontSize: "0.72rem",
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                            px: 1,
                          }}
                        >
                          USER PROOF DOCUMENT {index + 1}
                        </MDTypography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenViewModal(false);
              setCheckedImages({});
            }}
            color="primary"
          >
            Close
          </MDButton>
          <MDButton onClick={handleApprove} color="primary" disabled={!allChecked}>
            Approve
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Full-Screen Image Modal */}
      <Dialog open={Boolean(fullScreenImage)} onClose={() => setFullScreenImage(null)} maxWidth="xl">
        <DialogContent>
          <img
            src={fullScreenImage}
            alt="Full screen"
            style={{ width: "100%", height: "auto", borderRadius: "8px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullScreenImage(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openProfileModal}
        onClose={() => setOpenProfileModal(false)}
        fullWidth
        maxWidth="md"
      >
        {/* Header */}
        <DialogTitle
          sx={{
            backgroundColor: "#1976d2", // Blue background
            color: "#ffffff !important", // Force white text with !important
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.25rem",
            py: 2,
          }}
        >
          Vendor Profile Details
        </DialogTitle>

        {/* Content Area */}
        <DialogContent sx={{ padding: 3, backgroundColor: "#f5f5f5" }}>
          {profileData ? (
            <Paper
              elevation={3}
              sx={{
                padding: "24px",
                borderRadius: "12px",
                backgroundColor: "white",
              }}
            >
              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Name:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.vendor_name}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Email:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.vendor_email}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Mobile:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.mobile_number}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Business Name:
                  </MDTypography>
                  <MDTypography variant="body2">{profileData.business_name}</MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Business Type:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.business_type}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Business Category:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.business_category}
                  </MDTypography>
                </Grid>

                {/* Right Column */}

                <Grid item xs={12} md={6}>
                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Account Number:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.account_number}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    IFSC Code:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.ifsc_code}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Bank Name:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.bank_name}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    Branch Name:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.branch_name}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    PAN Card:
                  </MDTypography>
                  <MDTypography variant="body2" gutterBottom>
                    {profileData.pan_card_number}
                  </MDTypography>

                  <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                    GST Number:
                  </MDTypography>
                  <MDTypography variant="body2">{profileData.gst}</MDTypography>
                </Grid>
              </Grid>

              {/* Status Badge */}
              <Box
                sx={{
                  marginTop: 3,
                  textAlign: "center",
                  padding: "10px 16px",
                  backgroundColor: profileData.status === "Active" ? "#43a047" : "#e53935",
                  color: "white",
                  borderRadius: "8px",
                }}
              >
                <MDTypography variant="subtitle1" fontWeight="bold">
                  Status: {profileData.status}
                </MDTypography>
              </Box>
            </Paper>
          ) : (
            <MDTypography variant="body1" sx={{ color: "#d32f2f", textAlign: "center" }}>
              No vendor data available.
            </MDTypography>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            backgroundColor: "#e3f2fd",
            justifyContent: "center",
            py: 2,
          }}
        >
          <Button
            onClick={() => setOpenProfileModal(false)}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#115293",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Approve;